import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, KeyRound, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../App';
import { toast } from 'sonner';

/* P2.2 Returning-member upload: number + DOB (+ emailed one-time code when enabled by admin) -> scoped upload link */
export default function ReturningMemberUpload() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ membership_number: '', dob: '' });
  const [requestId, setRequestId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('details'); // details | code

  useEffect(() => { api.get('/api/membership/returning/config').then(setCfg).catch(() => setCfg({ require_code: true })); }, []);

  const go = (url) => {
    try { const u = new URL(url); navigate(u.pathname); } catch { window.location.href = url; }
  };

  const start = async (e) => {
    e.preventDefault();
    if (!form.membership_number.trim() || !form.dob) { toast.error('Please enter your membership number and date of birth'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/membership/returning/start', { membership_number: form.membership_number.trim(), dob: form.dob });
      if (r.step === 'upload' && r.upload_url) { go(r.upload_url); return; }
      setRequestId(r.request_id); setStep('code');
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) { toast.error('Enter the 6-digit code from your email'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/membership/returning/verify', { request_id: requestId, code });
      go(r.upload_url);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] bg-[hsl(var(--lux-ivory))] py-12 px-4" data-testid="returning-upload-page">
      <div className="max-w-lg mx-auto">
        <Card className="border-2 border-[hsl(var(--lux-gold))]">
          <CardContent className="p-7">
            <p className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--lux-gold))]">Existing members</p>
            <h1 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mt-1" style={{ fontFamily: 'Cormorant Garamond' }}>Upload your ID document</h1>
            {step === 'details' ? (
              <form onSubmit={start} className="mt-4 space-y-4" data-testid="returning-details-form">
                <p className="text-sm text-[hsl(var(--lux-ink)/0.8)]">Already have a GS membership number but have not yet sent us an identity document? Enter your details below — no new registration or payment is needed.</p>
                <div>
                  <Label htmlFor="ret-number">GS Membership Number</Label>
                  <Input id="ret-number" autoComplete="off" placeholder="e.g. GSNG0609260503XXXX" className="font-mono uppercase" value={form.membership_number} onChange={e => setForm({ ...form, membership_number: e.target.value.toUpperCase() })} data-testid="returning-number-input" />
                </div>
                <div>
                  <Label htmlFor="ret-dob">Date of birth</Label>
                  <Input id="ret-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} data-testid="returning-dob-input" />
                </div>
                <Button type="submit" className="w-full bg-[hsl(var(--lux-royal))] text-white" disabled={busy || !cfg} data-testid="returning-continue-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Continue</Button>
                {cfg?.require_code && <p className="text-[11px] text-[hsl(var(--lux-ink)/0.6)]">For your security we will email a one-time code to the address registered on your membership before showing the upload form.</p>}
              </form>
            ) : (
              <form onSubmit={verify} className="mt-4 space-y-4" data-testid="returning-code-form">
                <div className="p-3 rounded-lg bg-[hsl(var(--lux-parchment))] text-sm text-[hsl(var(--lux-ink)/0.85)] flex gap-2"><KeyRound className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--lux-royal))]" /><span>If those details match a membership record, a 6-digit code has been emailed to the address we hold. It is valid for 15 minutes and usually arrives within a few seconds &mdash; please also check your junk/spam folder. If you request a new code, only the newest code will work.</span></div>
                <div>
                  <Label htmlFor="ret-code">One-time code</Label>
                  <Input id="ret-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="font-mono tracking-[0.4em] text-lg text-center" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} data-testid="returning-code-input" />
                </div>
                <Button type="submit" className="w-full bg-[hsl(var(--lux-royal))] text-white" disabled={busy} data-testid="returning-verify-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Verify code</Button>
                <button type="button" className="text-xs underline text-[hsl(var(--lux-ink)/0.6)] flex items-center gap-1" onClick={() => { setStep('details'); setCode(''); }} data-testid="returning-back-btn"><ArrowLeft className="w-3 h-3" /> Start again</button>
              </form>
            )}
            <p className="mt-5 text-[11px] text-[hsl(var(--lux-ink)/0.55)]">Documents are stored privately and viewed only by authorised administrators. Need help? <Link to="/contact" className="underline">Contact us</Link>.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
